// Shared line calculations. Single source of truth so DocumentViewPage and
// UploadDocumentPage can't drift apart.
//
// Convention: items.deposit and items.purchase_tax are PER-LINE totals (the
// amount printed on the invoice for the whole line), NOT per-unit. The user
// reads them directly off the invoice — no division needed.
//
//   line_total   = (unit_price − discount) × qty + deposit + purchase_tax
//   credit_total = (returned units' share of effective unit price)
//                  + qty × credit_price_diff
// The net unit price keeps 3 decimals (a price per gram can be a fraction of an
// agora); line_total and credit_total are MONEY and keep 2, like the invoice
// prints them (owner, 14/10/2026 — they were 3 before). Mirrored by
// server/lib/estimatePrice.js, and enforced on save by server/lib/moneyPrecision.js.

import { round2 } from './money';

const round3 = (n) => Math.round(n * 1000) / 1000;

// Document grand total is kept to agorot (two decimals), like subtotal and vat,
// so the stored total is the invoice's own figure. It used to be rounded to a
// whole shekel here, which saved totals up to 49 agorot off the invoice (owner,
// 14/10/2026). Whole shekels are now a DISPLAY choice of the screens money is
// paid from (/suppliers, /credits) — formatCurrencyWhole in utils/api.js.
// Shared so UploadDocumentPage and DocumentViewPage round identically; mirrors
// computeDocTotal in server/lib/docTotal.js.
export const roundDocTotal = (n) => round2(n);

// A line saved before 14/10/2026 can carry a 3-decimal line_total /
// credit_total (they were round3 then). The edit form rounds them to the agora
// as it opens, so the subtotal, VAT and total it shows are the ones the save
// will store — server/lib/moneyPrecision.js rounds the same lines on save and
// the header follows them (#274). Without this, 3 × ₪1.005 showed 3.02 before
// saving and 3.03 after. Only these two money fields: prices keep 3 decimals.
// An empty value stays empty: a few readers tell "no total" from ₪0.
const money2dpOrEmpty = (v) => (v === null || v === undefined || v === '' ? v : round2(v));
export const lineMoney2dp = (it) => ({
  ...it,
  line_total: money2dpOrEmpty(it.line_total),
  credit_total: money2dpOrEmpty(it.credit_total),
});

export function calcNetUnitPrice(it) {
  const price = parseFloat(it.unit_price) || 0;
  const disc  = parseFloat(it.discount)   || 0;
  const net   = it.discount_type === 'amount' ? price - disc : price * (1 - disc / 100);
  return round3(net);
}

export function calcLineTotal(it) {
  const net  = calcNetUnitPrice(it);
  const qty  = parseFloat(it.qty)          || 0;
  const dep  = parseFloat(it.deposit)      || 0;
  const ptax = parseFloat(it.purchase_tax) || 0;
  return round2(net * qty + dep + ptax);
}

// Credit total has two independent parts:
//   1) returned units × effective unit price (net + per-unit share of deposit/tax)
//   2) full qty × per-unit price overcharge (credit_price_diff)
// Deposit and purchase_tax are stored per-line, so we share them proportionally
// across the line's qty before multiplying by credit_qty.
export function calcCreditTotal(it) {
  const net   = calcNetUnitPrice(it);
  const qty   = parseFloat(it.qty)               || 0;
  const dep   = parseFloat(it.deposit)           || 0;
  const ptax  = parseFloat(it.purchase_tax)      || 0;
  const cqty  = parseFloat(it.credit_qty)        || 0;
  const cdiff = parseFloat(it.credit_price_diff) || 0;
  const depPerUnit  = qty > 0 ? dep  / qty : 0;
  const ptaxPerUnit = qty > 0 ? ptax / qty : 0;
  const qtyCredit   = (net + depPerUnit + ptaxPerUnit) * cqty;
  const priceCredit = qty * cdiff;
  return round2(qtyCredit + priceCredit);
}
